import type { ReportColumn } from '../models/column.types'
import type { ReportRow } from '../models/report.types'

type EncodedIdentityValue =
  | ['array', EncodedIdentityValue[]]
  | ['bigint', string]
  | ['boolean', boolean]
  | ['circular']
  | ['null']
  | ['number', number | string]
  | ['object', Array<[string, EncodedIdentityValue]>]
  | ['string', string]
  | ['undefined']
  | ['unsupported', string]

const STRONG_IDENTITY_FIELDS = ['row_id', 'id', '_id', 'uuid', 'identity_key', 'row_key'] as const

function encodeIdentityValue(value: unknown, seen = new WeakSet<object>()): EncodedIdentityValue {
  if (value === null) return ['null']
  if (value === undefined) return ['undefined']
  if (typeof value === 'string') return ['string', value]
  if (typeof value === 'boolean') return ['boolean', value]
  if (typeof value === 'bigint') return ['bigint', String(value)]
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return ['number', 'NaN']
    if (value === Number.POSITIVE_INFINITY) return ['number', 'Infinity']
    if (value === Number.NEGATIVE_INFINITY) return ['number', '-Infinity']
    if (Object.is(value, -0)) return ['number', '-0']
    return ['number', value]
  }
  if (typeof value !== 'object') return ['unsupported', typeof value]
  if (seen.has(value)) return ['circular']
  seen.add(value)
  const encoded: EncodedIdentityValue = Array.isArray(value)
    ? ['array', value.map((item) => encodeIdentityValue(item, seen))]
    : [
        'object',
        Object.entries(value)
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([key, nested]) => [key, encodeIdentityValue(nested, seen)]),
      ]
  seen.delete(value)
  return encoded
}

function hasIdentityValue(row: ReportRow, field: string): boolean {
  const value = row[field]
  return value !== undefined && value !== null && value !== ''
}

function isIdField(field: string): boolean {
  return (
    field === 'id' ||
    field === '_id' ||
    field === 'uuid' ||
    field.endsWith('_id') ||
    field.endsWith('Id') ||
    field.endsWith('ID')
  )
}

function entityToken(field: string): string {
  return field
    .replace(/(?:_name|_label|_title|_id|Id|ID)$/u, '')
    .replaceAll('_', '')
    .toLowerCase()
}

function serializedTuple(parts: ReadonlyArray<readonly [string, unknown]>): string {
  return JSON.stringify(parts.map(([field, value]) => [field, encodeIdentityValue(value)]))
}

/** Stable source identity when the controller supplies an id or at least one dimension value. */
export function reportTableRowIdentity(
  row: ReportRow,
  columns: readonly ReportColumn[],
  breakdownField?: string | null,
): string | null {
  const strongField = STRONG_IDENTITY_FIELDS.find((field) => hasIdentityValue(row, field))
  if (strongField) return `report-row:${serializedTuple([[strongField, row[strongField]]])}`

  const fields = new Set([...Object.keys(row), ...columns.map((column) => column.field)])
  const idFields = [...fields]
    .filter((field) => isIdField(field) && hasIdentityValue(row, field))
    .sort((left, right) => left.localeCompare(right))
  const idEntities = new Set(idFields.map(entityToken))
  const dimensions = columns.flatMap((column) => {
    if (
      column.numeric ||
      column.isImage ||
      row[column.field] === undefined ||
      idFields.includes(column.field) ||
      idEntities.has(entityToken(column.field))
    )
      return []
    return [[column.field, row[column.field]] as const]
  })
  const identity = [...idFields.map((field) => [field, row[field]] as const), ...dimensions]
  if (identity.length === 0) return null
  const kind = breakdownField && row[breakdownField] !== undefined ? 'child' : 'row'
  return `report-${kind}:${serializedTuple(identity)}`
}

/** Stable identity for flat, matrix and expandable report rows, with an index fallback only. */
export function reportTableRowKey(
  row: ReportRow,
  index: number,
  columns: readonly ReportColumn[],
  breakdownField?: string | null,
): string {
  return reportTableRowIdentity(row, columns, breakdownField) ?? `report-row:index:${index}`
}

function nestedRows(row: ReportRow): ReportRow[] {
  return [
    ...(Array.isArray(row.edges) ? row.edges : []),
    ...(Array.isArray(row.breakdown_values) ? row.breakdown_values : []),
  ]
}

/** Build collision-free keys while retaining stable source identities whenever they are unique. */
export function createReportTableRowKeyResolver(
  rows: readonly ReportRow[],
  columns: readonly ReportColumn[],
  breakdownField?: string | null,
): (row: ReportRow, index: number) => string {
  const flattened: ReportRow[] = []
  const visit = (items: readonly ReportRow[]): void => {
    for (const row of items) {
      flattened.push(row)
      visit(nestedRows(row))
    }
  }
  visit(rows)

  const identities = flattened.map((row) => reportTableRowIdentity(row, columns, breakdownField))
  const totals = new Map<string, number>()
  for (const identity of identities) {
    if (identity) totals.set(identity, (totals.get(identity) ?? 0) + 1)
  }
  const occurrences = new Map<string, number>()
  const keys = new WeakMap<ReportRow, string>()
  flattened.forEach((row, index) => {
    const identity = identities[index]
    if (!identity) {
      keys.set(row, `report-row:index:${index}`)
      return
    }
    const occurrence = occurrences.get(identity) ?? 0
    occurrences.set(identity, occurrence + 1)
    keys.set(row, totals.get(identity) === 1 ? identity : `${identity}:occurrence:${occurrence}`)
  })
  return (row, index) => keys.get(row) ?? reportTableRowKey(row, index, columns, breakdownField)
}
